/**
 * hooks.js — Mappa dichiarativa degli eventi di tracking.
 *
 * Wrappa i metodi degli store Alpine.js con hook che emettono eventi
 * al dataLayer tramite il tracker. ZERO modifiche ai file degli store.
 *
 * Ogni hook verifica il successo dell'operazione prima di tracciare
 * (es. checkout.order?.id per purchase, auth.isLogged per login).
 *
 * Purchase: parte SOLO quando l'ordine è pagato o completato, mai alla
 * creazione (un ordine con bonifico o Satispay abbandonato non è una vendita).
 * event_id = purchase_{orderId}, lo stesso che il server manda alla Conversions
 * API: Meta deduplica Pixel e CAPI, e un refresh non conta due volte.
 */

import { patchMethod } from './patcher.js';
import { track, trackEcommerce, buildGA4Item, buildOrderItems, parseAmount, purchaseEventId, generateEventId, relay, contentCategory } from './tracker.js';

export function applyTrackingHooks(Alpine) {
    const cart     = Alpine.store('cart');
    const checkout = Alpine.store('checkout');
    const auth     = Alpine.store('auth');
    const wishlist = Alpine.store('wishlist');

    // ── Cart ──────────────────────────────────────────────

    // Contesto dell'aggiunta (da dove, con quale opzione, nome e categoria del
    // corso): lo deposita purchase-selector.js in cart._trackingContext prima di
    // chiamare addItem. Va letto e azzerato in ogni caso, anche su errore.
    const takeAddContext = () => {
        const ctx = cart._trackingContext || {};
        cart._trackingContext = null;
        return ctx;
    };

    patchMethod(cart, 'addItem', {
        onError: () => { takeAddContext(); },
        after: (result, [body]) => {
            const ctx = takeAddContext();

            // Auto-checkout gratuito: l'API ritorna orders_id, non è un add_to_cart
            // (tracciato come free_enrollment all'init della pagina di arrivo).
            if (result?.orders_id) return;

            // La risposta è il carrello intero: l'item aggiunto va cercato per id,
            // altrimenti nome e prezzo restano vuoti e value sarebbe tutto il carrello.
            const added = cart.items.find(i => String(i.id) === String(body?.courses_id)) || null;
            const item = buildGA4Item(added, body);
            // Il carrello risponde col titolo breve e, per gli item non trovati,
            // senza categoria: il selettore conosce titolo completo e categoria
            // del corso (gli stessi di view_item).
            if (ctx.item_name) item.item_name = ctx.item_name;
            if (ctx.item_category) item.item_category = ctx.item_category;
            const eventId = generateEventId(`addtocart_${item.item_id}`);
            trackEcommerce('add_to_cart', {
                currency: 'EUR',
                value: item.price,
                items: [item],
                event_id: eventId,
            }, {
                cta_location:       ctx.cta_location || 'unknown',
                pricing_option:     ctx.pricing_option,
                sheet_open_seconds: ctx.sheet_open_seconds,
                discount_type:      ctx.discount_type,
            });
            // Copia server-side (CAPI), stesso event_id: arriva anche se il Pixel è bloccato
            relay('AddToCart', {
                event_id: eventId,
                value: item.price,
                content_ids: [item.item_id],
                content_name: item.item_name,
                content_category: contentCategory([item]),
                num_items: 1,
            });
        },
    });

    patchMethod(cart, 'removeItem', {
        before: ([courseId]) => {
            // Prima della rimozione l'item è ancora in cart.items
            const removed = cart.items.find(i => String(i.id) === String(courseId)) || null;
            cart._trackingRemoved = removed ? buildGA4Item(removed) : { item_id: String(courseId), quantity: 1 };
        },
        after: () => {
            const item = cart._trackingRemoved || null;
            cart._trackingRemoved = null;
            if (!item) return;
            trackEcommerce('remove_from_cart', {
                currency: 'EUR',
                value: item.price || 0,
                items: [item],
            });
        },
    });

    patchMethod(cart, 'openDrawer', {
        after: () => {
            if (!cart.items.length) return;
            trackEcommerce('view_cart', {
                currency: 'EUR',
                value: cart.total,
                items: cart.items.map(i => buildGA4Item(i)),
            });
        },
    });

    // ── Checkout ──────────────────────────────────────────

    patchMethod(checkout, 'open', {
        after: () => {
            const items = cart.items.map(i => buildGA4Item(i));
            const eventId = generateEventId('checkout');
            trackEcommerce('begin_checkout', {
                currency: 'EUR',
                value: cart.total,
                items,
                event_id: eventId,
            });
            relay('InitiateCheckout', {
                event_id: eventId,
                value: cart.total,
                content_ids: items.map(i => i.item_id),
                content_category: contentCategory(items),
                num_items: items.length,
            });
        },
    });

    patchMethod(checkout, 'openSummary', {
        after: () => {
            // openSummary chiama _validate() e fa return se fallisce: in quel caso
            // summaryOpen non viene aperto (né schedulato) e submitError resta vuoto
            // ma i billingErrors sono valorizzati. Tracciamo solo se il form è valido.
            if (Object.keys(checkout.billingErrors || {}).length) return;
            const method = checkout.payments?.find(p => p.id === checkout.selectedPaymentId);
            const items = cart.items.map(i => buildGA4Item(i));
            const eventId = generateEventId('paymentinfo');
            trackEcommerce('add_payment_info', {
                currency: 'EUR',
                value: cart.total,
                payment_type: method?.name || 'unknown',
                items,
                event_id: eventId,
            });
            relay('AddPaymentInfo', {
                event_id: eventId,
                value: cart.total,
                content_ids: items.map(i => i.item_id),
                content_category: contentCategory(items),
                num_items: items.length,
            });
        },
    });

    // Purchase una sola volta per ordine, anche tra reload (ritorno da Satispay).
    const firedKey = (orderId) => `ik_purchase_fired_${orderId}`;
    const firePurchase = (order) => {
        const orderId = order?.id;
        if (!orderId) return;
        try {
            if (sessionStorage.getItem(firedKey(orderId))) return;
            sessionStorage.setItem(firedKey(orderId), '1');
        } catch (e) { /* storage non disponibile: al peggio Meta deduplica via event_id */ }

        const orderTotal = order.total;
        const totalValue = typeof orderTotal === 'object'
            ? parseAmount(orderTotal.gross !== undefined ? orderTotal.gross : orderTotal.total)
            : parseAmount(orderTotal);
        const taxValue = typeof orderTotal === 'object' ? parseAmount(orderTotal.tax) : 0;
        const items = buildOrderItems(order);

        if (totalValue <= 0) {
            // Iscrizione a zero (convenzione, coupon 100%): non è una vendita
            track('free_enrollment', { orders_id: String(orderId), items });
            return;
        }

        trackEcommerce('purchase', {
            transaction_id: String(orderId),
            currency: 'EUR',
            value: totalValue,
            tax: taxValue,
            items,
            event_id: purchaseEventId(orderId),
        });
    };

    patchMethod(checkout, 'confirm', {
        after: () => {
            // confirm() non lancia su errore — setta submitError.
            // checkout.order?.id è il segnale di successo.
            if (!checkout.order?.id) return;

            track('order_created', {
                orders_id: String(checkout.order.id),
                payment_required: !!checkout.order.payment_required,
            });

            // Già completato alla creazione: ordine a zero o coperto dal salvadanaio.
            if (checkout.order.payment_required === false) {
                firePurchase(checkout.order);
            }
        },
    });

    patchMethod(checkout, 'initPayment', {
        after: () => {
            if (checkout.paymentData) {
                track('payment_initiated', {
                    orders_id: checkout.order?.id,
                    circuit: checkout.paymentData.circuit || 'unknown',
                });
            }
        },
    });

    // ── Auth ──────────────────────────────────────────────

    patchMethod(auth, 'login', {
        after: () => {
            if (auth.isLogged) {
                track('login', { method: 'password' });
            }
        },
    });

    patchMethod(auth, 'verifyOtp', {
        after: () => {
            if (auth.isLogged) {
                track('login', { method: 'otp' });
            }
        },
    });

    patchMethod(auth, 'register', {
        after: (result) => {
            if (!result?.success) return;
            // event_id signup_{id}: lo stesso che ajax/register.php manda alla CAPI,
            // così Meta deduplica CompleteRegistration tra Pixel e server.
            const userId = result?.user?.id;
            // Se register() ha già impostato il flag di redirect, la pagina sta per
            // cambiare e il beacon si perderebbe: lo emette l'init della pagina di
            // arrivo, a cui lasciamo l'id utente.
            if (sessionStorage.getItem('ik_login_method') === 'register') {
                if (userId) sessionStorage.setItem('ik_signup_user_id', String(userId));
                return;
            }
            track('sign_up', {
                method: 'email',
                ...(userId ? { event_id: `signup_${userId}` } : {}),
            });
        },
    });

    // ── Wishlist ──────────────────────────────────────────

    if (wishlist) {
        patchMethod(wishlist, 'toggle', {
            after: (added, [courseId]) => {
                const eventId = generateEventId(`wishlist_${courseId}`);
                track(added ? 'add_to_wishlist' : 'remove_from_wishlist', {
                    courses_id: courseId,
                    event_id: eventId,
                });
                if (added) {
                    relay('AddToWishlist', { event_id: eventId, content_ids: [String(courseId)], num_items: 1 });
                }
            },
        });
    }

    // ── Catalogo: liste e click sulle card ────────────────

    // Pagina 1 del catalogo: view_item_list pushato lato server
    // (partials/tracking-view-item-list.php). Le pagine dello scroll infinito
    // arrivano da infinite-scroll.js con questo evento.
    window.addEventListener('ik:catalog-loaded', (e) => {
        const items = (e.detail?.items || []).map((c, i) => ({
            ...buildGA4Item(c),
            index: ((e.detail?.page || 2) - 1) * (e.detail?.pageSize || 24) + i,
        }));
        if (!items.length) return;
        trackEcommerce('view_item_list', {
            item_list_id: window.location.pathname,
            item_list_name: document.title,
            items,
        });
    });

    // select_item: click su un link dentro una card corso (SSR o JS). I dati
    // sono nei data-track-* della card; la navigazione parte subito dopo, GTM
    // invia con transport beacon.
    document.addEventListener('click', (e) => {
        const link = e.target.closest('.ik-course-card a[href]');
        if (!link) return;
        const card = link.closest('.ik-course-card');
        const id = card?.dataset?.trackId;
        if (!id) return;
        trackEcommerce('select_item', {
            item_list_id: window.location.pathname,
            item_list_name: document.title,
            items: [{
                item_id:       String(id),
                item_name:     card.dataset.trackName || '',
                item_category: card.dataset.trackCategory || card.dataset.trackType || 'course',
                item_variant:  card.dataset.trackCode || '',
                price:         parseFloat(card.dataset.trackPrice || 0) || 0,
                quantity:      1,
            }],
        });
    }, { capture: true });

    // ── Scheda corso: card articolo blog ──────────────────

    // Apertura dell'immagine dell'articolo (dalla card, dalla sidebar o dalla barra
    // mobile): dice quanto la copertina incuriosisce. Arriva da article-card.js.
    window.addEventListener('ik:article-preview', (e) => {
        const course = window.IKOS?.course || {};
        track('blog_preview_open', {
            course_id:   String(course.id ?? ''),
            course_code: course.code || '',
            origin:      e.detail?.origin || 'card',
        });
    });

    // Click sul link all'articolo: è un'uscita dalla pagina di acquisto, da leggere
    // insieme alle conversioni della scheda. GTM invia con transport beacon.
    document.addEventListener('click', (e) => {
        const link = e.target.closest('.ik-article-card__caption a[href]');
        if (!link) return;
        const course = window.IKOS?.course || {};
        track('blog_article_click', {
            course_id:   String(course.id ?? ''),
            course_code: course.code || '',
            link_url:    link.href,
        });
    }, { capture: true });

    // ── Scheda corso: decisione d'acquisto ────────────────
    //
    // Eventi emessi da purchase-selector.js (canale ik:purchase, campo type),
    // dal consent store (ik:consent-choice) e dal DOM della scheda. Servono a
    // leggere i passaggi tra view_item e add_to_cart, che prima erano invisibili:
    // apertura del bottom sheet, opzione scelta, codici, FAQ, sezioni raggiunte.

    const course = () => window.IKOS?.course || {};

    const scrollDepthPct = () => {
        const doc = document.documentElement;
        const total = Math.max(1, (doc.scrollHeight || 0) - window.innerHeight);
        return Math.min(100, Math.round(((window.scrollY || 0) / total) * 100));
    };

    const pageContext = () => ({
        course_id:        String(course().id ?? ''),
        course_code:      course().code || '',
        course_type:      course().category || course().type || '',
        page_type:        window.dataLayer?.find?.(e => e && e.ik_page)?.ik_page?.type || (course().id ? 'course' : 'other'),
        scroll_depth_pct: scrollDepthPct(),
        seconds_on_page:  Math.round(performance.now() / 1000),
    });

    const consentStore = Alpine.store('consent');

    const PURCHASE_EVENTS = {
        sheet_open:          'purchase_sheet_open',
        sheet_close:         'purchase_sheet_close',
        option_select:       'pricing_option_select',
        discount_toggle_open:'discount_toggle_open',
        discount_apply:      'discount_apply',
    };

    window.addEventListener('ik:purchase', (e) => {
        const { type, ...detail } = e.detail || {};
        const name = PURCHASE_EVENTS[type];
        if (!name) return;
        const data = { ...pageContext(), ...detail };
        // course_id/code/type arrivano dal selettore (nel drawer del catalogo il
        // corso non è window.IKOS.course): quelli del detail vincono.
        if (detail.course_id != null) data.course_id = String(detail.course_id);
        if (type === 'sheet_open') data.banner_visible = !!consentStore?.bannerVisible;
        track(name, data);
    });

    window.addEventListener('ik:consent-choice', (e) => {
        track('cookie_consent_choice', {
            page_type: pageContext().page_type,
            ...(e.detail || {}),
        });
    });

    // FAQ aperte: una sola riga per apertura (la chiusura non interessa).
    document.addEventListener('click', (e) => {
        const header = e.target.closest('.ik-course-faq-box .ik-accordion__header');
        if (!header) return;
        const item = header.closest('.ik-accordion__item');
        if (item?.classList.contains('ik-accordion__item--open')) return;
        const list = item?.parentElement;
        const index = list ? Array.prototype.indexOf.call(list.children, item) : -1;
        track('faq_open', {
            ...pageContext(),
            faq_index: index,
            faq_question: (header.querySelector('.ik-accordion__title')?.textContent || '').trim().slice(0, 100),
        });
    }, { capture: true });

    // "A chi è rivolto": apertura delle discipline nascoste di un gruppo.
    document.addEventListener('click', (e) => {
        const button = e.target.closest('.ik-course-disciplines__group button.ik-badge--action');
        if (!button) return;
        const group = button.closest('.ik-course-disciplines__group');
        track('professions_expand', {
            ...pageContext(),
            group: (group?.querySelector('.ik-course-disciplines__title')?.textContent || '').trim().slice(0, 60),
        });
    }, { capture: true });

    // Sezioni raggiunte: una volta per sezione, quando il suo bordo superiore
    // entra nel 60% alto del viewport (l'utente l'ha portata in posizione di
    // lettura). Soglia sull'elemento intero non va bene: le sezioni più alte
    // del viewport (chip professioni, programma) non sarebbero mai "visibili".
    const observeSections = () => {
        const nodes = document.querySelectorAll('[data-track-section]');
        if (!nodes.length || !('IntersectionObserver' in window)) return;
        const seen = new Set();
        const io = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                const key = entry.target.dataset.trackSection;
                if (!key || seen.has(key)) return;
                seen.add(key);
                io.unobserve(entry.target);
                track('section_view', { ...pageContext(), section: key });
            });
        }, { rootMargin: '0px 0px -40% 0px', threshold: 0 });
        nodes.forEach(n => io.observe(n));
    };
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', observeSections, { once: true });
    } else {
        observeSections();
    }

    // ── Payment Completed (PayPal/Satispay) ──────────────

    // checkout.orderPaid viene settato a true dopo PayPal capture riuscito
    // o al ritorno da Satispay (polling payment-status). Usiamo Alpine.effect()
    // perché il cambio avviene in un callback interno non wrappabile.
    // È QUI che parte il purchase per gli ordini con pagamento online.
    // orderPaid ora passa a true SOLO dopo conferma server-side (il query param di
    // ritorno non lo imposta più). confirmPaidThen resta come difesa in profondità:
    // ri-verifica lo stato reale prima di tracciare, salvo ordine già pagato.
    let _prevOrderPaid = false;
    const confirmPaidThen = (order, fn) => {
        if (order.payment_required === false) { fn(); return; }
        fetch(`${window.IKOS.ajaxBase}/payment-status.php?order_id=${encodeURIComponent(order.id)}`, {
            headers: { 'Accept': 'application/json' },
        })
            .then(r => r.json().catch(() => ({})))
            .then(res => { if (res && res.paid) fn(); })
            .catch(() => {});
    };
    Alpine.effect(() => {
        const paid = checkout.orderPaid;
        if (paid && !_prevOrderPaid && checkout.order?.id) {
            const order = checkout.order;
            confirmPaidThen(order, () => {
                track('payment_completed', {
                    orders_id: order.id,
                    circuit: checkout.paymentData?.circuit || 'unknown',
                });
                firePurchase(order);
            });
        }
        _prevOrderPaid = paid;
    });

    // ── Eventi post-redirect ──────────────────────────────
    //
    // I flussi che fanno `window.location.href = ...` subito dopo il successo
    // distruggono il contesto JS prima che il beacon parta. I punti-sorgente
    // lasciano un flag in sessionStorage PRIMA del redirect; qui, all'init della
    // pagina di arrivo, lo leggiamo e tracciamo.

    // Login (e registrazione con redirect). Punti sorgente di `ik_login_method`:
    //   auth.js login() → 'password', verifyOtp() → 'otp', register() → 'register',
    //   auth-drawer.js → 'oauth', register-drawer.js → 'register'.
    // 'register' conta come login (la registrazione è anche un accesso) e produce
    // anche `sign_up`, che il patchMethod su auth.register ha saltato apposta.
    const _loginMethod = sessionStorage.getItem('ik_login_method');
    if (_loginMethod) {
        track('login', { method: _loginMethod });
        if (_loginMethod === 'register') {
            const signupUserId = sessionStorage.getItem('ik_signup_user_id');
            sessionStorage.removeItem('ik_signup_user_id');
            track('sign_up', {
                method: 'email',
                ...(signupUserId ? { event_id: `signup_${signupUserId}` } : {}),
            });
        }
        sessionStorage.removeItem('ik_login_method');
    }

    // Iscrizione gratuita da catalogo (cart.js: auto-checkout → redirect)
    const _freeOrder = sessionStorage.getItem('ik_free_order');
    if (_freeOrder) {
        sessionStorage.removeItem('ik_free_order');
        try {
            const data = JSON.parse(_freeOrder);
            track('free_enrollment', {
                orders_id: String(data.orders_id || ''),
                items: data.courses_id ? [{ item_id: String(data.courses_id), quantity: 1, price: 0 }] : [],
            });
        } catch (e) { /* payload non valido */ }
    }
}
